import React, { useState, useCallback, useEffect } from 'react';
import { TATBESTAENDE, TATBESTAND_MAP } from '../data/tatbestaende';
import { toMandantID, type MandantID } from '../../../types/branded';
import { useMandantStore } from '../../../store/mandantStore';
import {
  loadArbeitsplaetze, saveArbeitsplatz, deleteArbeitsplatz,
} from '../../../services/vorsorgeService';

// ── Typen ──────────────────────────────────────────────────────────────────

export interface Arbeitsplatz {
  id?:          string;
  mandantId:    MandantID;
  bezeichnung:  string;
  abteilung:    string;
  tatbestandCodes: string[];   // VGÜ-Tatbestand-Codes
  beschreibung?: string | undefined;
  aktiv:        boolean;
  erstelltAm:   string;       // ISO-Date
}

// ── Formular ───────────────────────────────────────────────────────────────

interface FormState {
  bezeichnung:    string;
  abteilung:      string;
  tatbestandCodes: string[];
  beschreibung:   string;
}

const INITIAL_FORM: FormState = {
  bezeichnung:    '',
  abteilung:      '',
  tatbestandCodes: [],
  beschreibung:   '',
};

function ArbeitsplatzForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: FormState | undefined;
  onSave: (form: FormState) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [form, setForm] = useState<FormState>(initial ?? INITIAL_FORM);
  const [tatFilter, setTatFilter] = useState('');

  const toggleTat = (code: string): void => {
    setForm(f => ({
      ...f,
      tatbestandCodes: f.tatbestandCodes.includes(code)
        ? f.tatbestandCodes.filter(c => c !== code)
        : [...f.tatbestandCodes, code],
    }));
  };

  const filteredTats = TATBESTAENDE.filter(t => {
    const q = tatFilter.toLowerCase();
    return q === '' || t.bezeichnung.toLowerCase().includes(q) || t.code.toLowerCase().includes(q);
  });

  const valid = form.bezeichnung.trim().length > 0;

  return (
    <div className="space-y-5">
      {/* Basisdaten */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="label-text">Bezeichnung *</label>
          <input
            type="text"
            value={form.bezeichnung}
            onChange={e => { setForm(f => ({ ...f, bezeichnung: e.target.value })); }}
            placeholder="z.B. Schweißer, Büroarbeitsplatz…"
            className="input-field"
          />
        </div>
        <div className="space-y-1">
          <label className="label-text">Abteilung</label>
          <input
            type="text"
            value={form.abteilung}
            onChange={e => { setForm(f => ({ ...f, abteilung: e.target.value })); }}
            placeholder="z.B. Produktion, Verwaltung…"
            className="input-field"
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="label-text">Beschreibung</label>
        <textarea
          value={form.beschreibung}
          onChange={e => { setForm(f => ({ ...f, beschreibung: e.target.value })); }}
          rows={2}
          placeholder="Tätigkeitsbeschreibung, Gefährdungshinweise…"
          className="input-field resize-none"
        />
      </div>

      {/* Tatbestände */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="label-text">
            VGÜ-Tatbestände
            {form.tatbestandCodes.length > 0 && (
              <span className="ml-2 text-xs bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 px-1.5 py-0.5 rounded-full font-medium">
                {form.tatbestandCodes.length} gewählt
              </span>
            )}
          </label>
          {form.tatbestandCodes.length > 0 && (
            <button
              type="button"
              onClick={() => { setForm(f => ({ ...f, tatbestandCodes: [] })); }}
              className="text-xs text-red-500 hover:text-red-700"
            >
              Alle entfernen
            </button>
          )}
        </div>

        <input
          type="search"
          value={tatFilter}
          onChange={e => { setTatFilter(e.target.value); }}
          placeholder="Tatbestand suchen…"
          className="input-field text-sm"
        />

        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
          {filteredTats.map(t => (
            <label
              key={t.code}
              className={`flex items-start gap-3 px-4 py-2.5 cursor-pointer transition-colors
                ${form.tatbestandCodes.includes(t.code)
                  ? 'bg-blue-50 dark:bg-blue-950/30'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                }
                border-b border-gray-100 dark:border-gray-800 last:border-0`}
            >
              <input
                type="checkbox"
                checked={form.tatbestandCodes.includes(t.code)}
                onChange={() => { toggleTat(t.code); }}
                className="mt-0.5 rounded border-gray-300 text-blue-600 flex-shrink-0"
              />
              <div className="min-w-0">
                <span className="font-mono text-xs font-bold text-gray-500 dark:text-gray-400">{t.code}</span>
                <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">{t.bezeichnung}</span>
              </div>
            </label>
          ))}
          {filteredTats.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4 italic">Keine Tatbestände gefunden.</p>
          )}
        </div>
      </div>

      {/* Aktionen */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={() => { onSave(form); }}
          disabled={!valid}
          className="btn-primary flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Speichern
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary flex-1 justify-center">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

// ── Karte ──────────────────────────────────────────────────────────────────

function ArbeitsplatzKarte({
  ap,
  onEdit,
  onDelete,
}: {
  ap: Arbeitsplatz;
  onEdit: () => void;
  onDelete: () => void;
}): React.JSX.Element {
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <div className="card space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{ap.bezeichnung}</h3>
          {ap.abteilung && (
            <p className="text-xs text-gray-500 mt-0.5">{ap.abteilung}</p>
          )}
        </div>
        <div className="flex gap-1">
          <button
            onClick={onEdit}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
            title="Bearbeiten"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => { setShowConfirm(true); }}
            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-gray-400 hover:text-red-500 transition-colors"
            title="Löschen"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tatbestände */}
      {ap.tatbestandCodes.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {ap.tatbestandCodes.map(code => {
            const t = TATBESTAND_MAP.get(code);
            return (
              <span
                key={code}
                className="text-xs bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-md font-medium"
                title={t?.bezeichnung}
              >
                {code}
              </span>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-gray-400 italic">Keine Tatbestände zugeordnet</p>
      )}

      {ap.beschreibung && (
        <p className="text-xs text-gray-500 border-t border-gray-100 dark:border-gray-800 pt-2 line-clamp-2">
          {ap.beschreibung}
        </p>
      )}

      {/* Lösch-Bestätigung */}
      {showConfirm && (
        <div className="border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 rounded-xl p-3 space-y-2">
          <p className="text-sm text-red-700 dark:text-red-400">Arbeitsplatz wirklich löschen?</p>
          <div className="flex gap-2">
            <button onClick={onDelete} className="text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700">
              Ja, löschen
            </button>
            <button onClick={() => { setShowConfirm(false); }} className="text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-lg">
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────

export function ArbeitsplatzVerwaltung(): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const [arbeitsplaetze, setArbeitsplaetze] = useState<Arbeitsplatz[]>([]);
  const [showForm, setShowForm]             = useState(false);
  const [editItem, setEditItem]             = useState<Arbeitsplatz | null>(null);

  useEffect(() => {
    void loadArbeitsplaetze(mandant?.id).then(setArbeitsplaetze);
  }, [mandant?.id]);

  const reload = (): void => { void loadArbeitsplaetze(mandant?.id).then(setArbeitsplaetze); };

  const handleSave = useCallback((form: FormState): void => {
    const ap: Arbeitsplatz = editItem !== null
      ? { ...editItem, ...form }
      : {
          id:              crypto.randomUUID(),
          mandantId:       toMandantID(mandant !== null ? mandant.id : ''),
          bezeichnung:     form.bezeichnung,
          abteilung:       form.abteilung,
          tatbestandCodes: form.tatbestandCodes,
          beschreibung:    form.beschreibung !== '' ? form.beschreibung : undefined,
          aktiv:           true,
          erstelltAm:      new Date().toISOString().slice(0, 10),
        };
    void saveArbeitsplatz(ap).then(() => {
      reload();
      setEditItem(null);
      setShowForm(false);
    });
  }, [editItem, mandant]);

  const handleDelete = useCallback((ap: Arbeitsplatz): void => {
    if (ap.id !== undefined) {
      void deleteArbeitsplatz(ap.id).then(reload);
    }
  }, []);

  const handleEdit = useCallback((ap: Arbeitsplatz): void => {
    setEditItem(ap);
    setShowForm(true);
  }, []);

  return (
    <div className="p-6 space-y-5 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Arbeitsplätze</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            VGÜ-Tatbestände pro Arbeitsplatz hinterlegen
            {mandant !== null && mandant !== undefined && (
              <span className="ml-2 font-medium" style={{ color: mandant.farbe }}>{mandant.name}</span>
            )}
          </p>
        </div>
        <button
          onClick={() => { setEditItem(null); setShowForm(true); }}
          className="btn-primary"
        >
          + Arbeitsplatz
        </button>
      </div>

      {/* Formular */}
      {showForm && (
        <div className="card">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {editItem !== null ? 'Arbeitsplatz bearbeiten' : 'Neuer Arbeitsplatz'}
          </h2>
          <ArbeitsplatzForm
            initial={editItem !== null ? {
              bezeichnung:    editItem.bezeichnung,
              abteilung:      editItem.abteilung,
              tatbestandCodes: editItem.tatbestandCodes,
              beschreibung:   editItem.beschreibung ?? '',
            } as FormState : undefined}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditItem(null); }}
          />
        </div>
      )}

      {/* Liste */}
      {arbeitsplaetze.length === 0 && !showForm && (
        <div className="card text-center py-12 space-y-3">
          <p className="text-4xl">🏭</p>
          <p className="font-medium text-gray-600 dark:text-gray-400">Noch keine Arbeitsplätze angelegt</p>
          <p className="text-sm text-gray-400">Legen Sie Arbeitsplätze an und ordnen Sie VGÜ-Tatbestände zu.</p>
          <button onClick={() => { setShowForm(true); }} className="btn-primary mx-auto">
            Ersten Arbeitsplatz anlegen
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {arbeitsplaetze.map((ap, idx) => (
          <ArbeitsplatzKarte
            key={idx}
            ap={ap}
            onEdit={() => { handleEdit(ap); }}
            onDelete={() => { handleDelete(ap); }}
          />
        ))}
      </div>

      {arbeitsplaetze.length > 0 && (
        <p className="text-xs text-gray-400 text-center">
          ✅ Persistenz in Dexie v5 · {arbeitsplaetze.length} Arbeitsplatz{arbeitsplaetze.length !== 1 ? 'plätze' : ''}
        </p>
      )}
    </div>
  );
}
